import {useEffect,useRef} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import {getServerTime,type AwardStreamBoundary} from '@platform/client';
import {cachedGameAudioSource,createGameAudio} from './gameAudio';

// Observe the current sale-close transition once. Refresh/reconnect baselines,
// historical views and future sale offers never replay the opening sequence.
export function useDrawStartSound(state:Snapshot|null,sound:boolean,stream:AwardStreamBoundary,source:string,countdownSource:string){
 const previous=useRef<{drawId:string;status:string;generation:number}|null>(null);
 const active=useRef<{drawId:string;generation:number;cancel:()=>void;start:()=>void}|null>(null);
 useEffect(()=>{
  // Warm the same cached asset while sales are open so the first percussion
  // attack is not lost to a download at the ten-second boundary.
  if(sound&&stream.ready&&state?.status==='SalesOpen'&&!state.isUpcoming&&!document.hidden)
   for(const clip of [countdownSource,source])void cachedGameAudioSource(clip).catch(()=>{});
 },[sound,stream.ready,state?.status,state?.isUpcoming,countdownSource,source]);
 useEffect(()=>{
  const before=previous.current;
  previous.current=state&&stream.ready?{drawId:state.drawId,status:state.status,generation:stream.generation}:null;
  if(!state||!stream.ready||!sound||state.isUpcoming||state.x.paused||document.hidden||
   active.current&&(active.current.drawId!==state.drawId||active.current.generation!==stream.generation)||
   state.status!=='Preparing'&&state.status!=='Live')active.current?.cancel();
  if(state?.status==='Live')active.current?.start();
  if(!state||!stream.ready||!before||before.generation!==stream.generation||before.drawId!==state.drawId||
   before.status!=='SalesOpen'||state.status!=='Preparing'||state.isUpcoming||state.x.paused||!sound||document.hidden||!state.drawStartsAtUtc)return;
  const deadline=Date.parse(state.drawCountdownEndsAtUtc??state.drawStartsAtUtc);if(!Number.isFinite(deadline))return;
  let audio:ReturnType<typeof createGameAudio>|undefined,started=false;
  const timers:number[]=[];
  const remaining=()=>deadline-getServerTime(state);
  const start=()=>{
   if(started||active.current!==sequence)return;started=true;
   timers.forEach(clearTimeout);
   audio=createGameAudio(source,{finishBeforeNumbers:true});void audio.play().catch(()=>{});
  };
  const hide=()=>{if(document.hidden)sequence.cancel();};
  const sequence={drawId:state.drawId,generation:stream.generation,start,cancel:()=>{
   timers.forEach(clearTimeout);audio?.pause();document.removeEventListener('visibilitychange',hide);
   if(active.current===sequence)active.current=null;
  }};
  active.current=sequence;document.addEventListener('visibilitychange',hide);
  const countdown=()=>{
   if(active.current!==sequence)return;
   if(remaining()<=0){start();return;}
   void cachedGameAudioSource(countdownSource).then(cached=>{
    if(active.current!==sequence||started||remaining()<=0)return;
    audio=createGameAudio(cached,{kind:'countdown',startAtSeconds:()=>Math.max(0,10-remaining()/1000),finishBeforeNumbers:true});
    return audio.play();
   }).catch(()=>{});
  };
  if(remaining()<=10000)countdown();else timers.push(window.setTimeout(countdown,remaining()-10000));
  if(remaining()>0)timers.push(window.setTimeout(start,remaining()));
 },[state?.drawId,state?.status,state?.isUpcoming,state?.x.paused,state?.drawStartsAtUtc,state?.drawCountdownEndsAtUtc,sound,stream.ready,stream.generation,source,countdownSource]);
 useEffect(()=>()=>active.current?.cancel(),[]);
}
